/**
 * Utilitário para obter informações de versão do sistema.
 *
 * A versão vem do `package.json`, injetada em build-time pelo Vite via
 * `define: { __APP_VERSION__, __APP_MODE__, __APP_SHOW_ENV_BADGE__ }`
 * (declarados em `src/vite-env.d.ts`). Esses três globals equivalem,
 * no bundle final, a `import.meta.env.MODE` + `import.meta.env
 * .VITE_SHOW_ENV_BADGE`, mas sem disparar SyntaxError em Jest+CJS.
 * Este módulo é a fonte única consumida pelo app (login, lista, Sentry),
 * portanto login, lista e Sentry nunca mais divergem por hardcoded literal.
 */

declare const __APP_VERSION__: string
declare const __APP_MODE__: string
declare const __APP_SHOW_ENV_BADGE__: string

/**
 * Retorna a versão atual do sistema (sem sufixo de ambiente).
 * @example "v2.0.3"
 */
export const getSystemVersion = (): string => `v${__APP_VERSION__}`

/**
 * Retorna a versão completa com sufixo de ambiente quando o badge
 * de ambiente está habilitado (`VITE_SHOW_ENV_BADGE=true`).
 *
 * O sufixo vem do `mode` usado no build (`vite build --mode staging`),
 * portanto não há string hardcoded no código. Em produção (badge
 * desabilitado por padrão) retorna apenas `v2.0.3`.
 *
 * @example "v2.0.3"            // badge desabilitado ou MODE ausente
 * @example "v2.0.3 — staging" // badge habilitado e mode=staging
 */
export const getSystemVersionWithEnv = (): string => {
  const base = getSystemVersion()
  const showBadge = __APP_SHOW_ENV_BADGE__ === 'true'
  if (!showBadge) return base
  const mode = String(__APP_MODE__ ?? '').trim()
  return mode ? `${base} — ${mode}` : base
}

/**
 * Retorna a versão completa com informações adicionais
 * @returns objeto com informações detalhadas da versão
 */
export const getVersionInfo = () => {
  return {
    version: __APP_VERSION__,
    formattedVersion: getSystemVersion(),
    formattedVersionWithEnv: getSystemVersionWithEnv(),
    buildDate: new Date().toISOString(),
  }
}
